import { CircleSlash, Hourglass, MailCheck, TriangleAlert, UserMinus } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import AuthHead from '../auth/AuthHead'
import AuthLayout from '../auth/AuthLayout'
import Button from '../../components/Button'
import PageLoader from '../../components/PageLoader'
import { signOut } from '../../lib/api'
import { useResource } from '../../lib/useResource'
import { confirmEmail, getStatusMessage } from './activateService'
import { activateStrings as A } from './strings'
import './ActivatePage.css'

/**
 * The glyph per state. An envelope is right for "we emailed you" and wrong for
 * "this account is closed" — the same badge on all five would tell the two apart
 * only by reading, which is the one thing a status page should not require.
 */
const MARKS = {
  confirm: MailCheck,
  waitingUser: MailCheck,
  waitingAdmin: Hourglass,
  expired: Hourglass,
  resigned: UserMinus,
  suspended: CircleSlash,
}

/**
 * The one surface for every account state that stops a user getting in, plus the
 * activation link itself. A title, the account-state message, and a way out.
 *
 * SIX STATES, TWO KINDS:
 *
 *   `confirm`  — `/activate?id=…&t=…`, the link in an activation email. Opening it
 *                CONFIRMS the address: the message is the outcome of that call, not a
 *                caption, so there is nothing sensible to show until it answers.
 *   the rest   — where `ForceRedirectionMiddleware` sends a blocked account, and where
 *                `Gate` in `/api/session` routes it once the SPA is running (SessionGate).
 *                The message is fetched so an administrator editing the resource file
 *                changes what the product says (W6), with the bundled copy shown until it
 *                arrives so the screen never flashes empty.
 *
 * @param {'confirm'|'waitingUser'|'waitingAdmin'|'expired'|'resigned'|'suspended'} state
 */
export default function ActivatePage({ state }) {
  const Mark = MARKS[state] ?? MailCheck
  const isConfirm = state === 'confirm'

  const [params] = useSearchParams()
  const id = params.get('id') ?? params.get('Id')
  const token = params.get('t') ?? params.get('T')

  /* NO LINK, NO REQUEST. With neither parameter present both are `null` and the service
     interpolated them anyway, so a bare `/activate` asked the server to confirm
     `?id=null&t=null` — which it answered 500. There is nothing to confirm without a link,
     and the empty result falls into the failure branch below, whose wording ("please check
     the link") is already the right thing to say. */
  const hasLink = Boolean(id && token)

  const { data, loading, error } = useResource(
    (signal) => {
      if (!isConfirm) return getStatusMessage(state, signal)
      return hasLink ? confirmEmail({ id, token }, signal) : Promise.resolve('')
    },
    [state, id, token],
  )

  /* The link's own result has no bundled equivalent — it depends on the token. The five
     status screens fall back to their shipped wording, which is the same sentence. */
  const message = data || (isConfirm ? '' : A.states[state])

  return (
    <AuthLayout>
      <div className="auth__card">
        <AuthHead
          title={A.title}
          mark={
            <span className="activate__badge" aria-hidden="true">
              <Mark size={22} />
            </span>
          }
        />
        <div className="auth__body activate">
          {isConfirm && loading ? (
            <PageLoader />
          ) : isConfirm && (error || !message) ? (
            <p className="auth__error" role="alert">
              <TriangleAlert size={16} aria-hidden="true" />
              <bdi>{A.confirmFailed}</bdi>
            </p>
          ) : (
            <p className="activate__message">
              <bdi>{message}</bdi>
            </p>
          )}

          {/* Signing out is the only way forward from a blocked account, and it has to
              end the SERVER session — see signOut(). */}
          <Button variant="soft" onClick={signOut}>
            {isConfirm ? A.goToLogin : A.logout}
          </Button>
        </div>
      </div>
    </AuthLayout>
  )
}
